import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { Label } from "./label";

interface FieldProps {
  label: string;
  /** Inline error from the server or client validation. Announced to screen readers. */
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactElement<{ id?: string; "aria-invalid"?: boolean; "aria-describedby"?: string }>;
}

/** Label + control + hint/error, wired with ids and aria attributes. */
export const Field = ({ label, error, hint, children }: FieldProps) => {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      })
    : children;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {control}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-subtle">
          {hint}
        </p>
      )}
      <p id={`${id}-error`} className="min-h-0 text-xs text-danger" aria-live="polite">
        {error}
      </p>
    </div>
  );
};

/** Form-level error banner, announced politely. */
export const FormError = ({ message }: { message?: string | undefined }) => (
  <div aria-live="polite">
    {message && (
      <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
        {message}
      </p>
    )}
  </div>
);
